import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { trackWhatsAppClick } from '../utils/analytics';

// Reads from env at build time; falls back to the office WhatsApp number
// used elsewhere (Contact page, Footer) so the click always routes correctly.
const WHATSAPP_NUMBER = process.env.REACT_APP_WHATSAPP_NUMBER || '971501219341';

const DEFAULT_MESSAGE =
  "Hello White Paper team, I'd like to speak with an advisor about your audit, tax and advisory services.";

// Official WhatsApp glyph. Inline SVG so we do not depend on lucide's outline
// icon and the brand mark stays crisp on the coloured pill.
const WhatsAppGlyph = ({ size = 28, className = '' }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 32 32"
    width={size}
    height={size}
    aria-hidden="true"
    className={className}
    fill="currentColor"
  >
    <path d="M19.11 17.21c-.29-.15-1.7-.84-1.96-.93-.26-.1-.45-.14-.64.14-.19.29-.74.93-.9 1.12-.17.19-.33.21-.62.07-.29-.15-1.22-.45-2.32-1.43-.86-.76-1.44-1.71-1.6-2-.17-.29-.02-.45.12-.6.13-.13.29-.33.43-.5.14-.17.19-.29.29-.48.1-.19.05-.36-.02-.5-.07-.14-.64-1.55-.88-2.12-.23-.55-.47-.48-.64-.49l-.55-.01c-.19 0-.5.07-.76.36s-1 .98-1 2.39 1.02 2.77 1.17 2.96c.14.19 2.01 3.07 4.88 4.31.68.29 1.22.46 1.63.59.68.22 1.31.19 1.8.11.55-.08 1.7-.69 1.94-1.36.24-.67.24-1.24.17-1.36-.07-.12-.26-.19-.55-.34zM16.02 5.33c-5.9 0-10.7 4.79-10.7 10.68 0 1.88.5 3.72 1.44 5.34l-1.53 5.61 5.74-1.51a10.69 10.69 0 0 0 5.04 1.28h.01c5.9 0 10.7-4.79 10.7-10.68a10.6 10.6 0 0 0-3.13-7.56 10.62 10.62 0 0 0-7.57-3.16zm0 19.51h-.01a8.85 8.85 0 0 1-4.52-1.24l-.32-.19-3.4.89.91-3.31-.21-.34a8.85 8.85 0 0 1-1.36-4.71c0-4.9 4-8.88 8.91-8.88a8.87 8.87 0 0 1 8.9 8.9c0 4.9-4 8.88-8.9 8.88z" />
  </svg>
);

const WhatsAppFloat = () => {
  const [showBubble, setShowBubble] = useState(false);
  const [dismissed, setDismissed] = useState(true);

  // Reveal the prompt bubble a few seconds after the page loads once per
  // session so it does not nag returning visitors on every navigation.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const seen = window.sessionStorage?.getItem('wpaa_wa_prompt_seen');
    if (seen) return;
    setDismissed(false);
    const t = setTimeout(() => setShowBubble(true), 3500);
    return () => clearTimeout(t);
  }, []);

  const dismissBubble = (e) => {
    e?.stopPropagation();
    setShowBubble(false);
    setDismissed(true);
    try {
      window.sessionStorage?.setItem('wpaa_wa_prompt_seen', '1');
    } catch {
      /* sessionStorage unavailable — safe to ignore */
    }
  };

  const handleClick = () => {
    trackWhatsAppClick('floating_button');
    dismissBubble();
    const text = encodeURIComponent(DEFAULT_MESSAGE);
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${text}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2"
      data-testid="whatsapp-float-container"
    >
      {/* Prompt bubble */}
      {showBubble && !dismissed && (
        <div
          className="relative max-w-[16rem] rounded-2xl bg-white shadow-2xl border border-gray-100 px-4 py-3 pr-8 animate-in fade-in slide-in-from-bottom-2"
          data-testid="whatsapp-float-bubble"
        >
          <button
            type="button"
            onClick={dismissBubble}
            aria-label="Dismiss WhatsApp prompt"
            className="absolute top-1.5 right-1.5 text-gray-400 hover:text-gray-700 transition-colors"
            data-testid="whatsapp-float-bubble-close"
          >
            <X size={14} />
          </button>
          <p className="text-[13px] font-semibold text-gray-900 leading-snug">
            Need a quick answer?
          </p>
          <p className="text-xs text-gray-500 mt-0.5 leading-snug">
            Chat with our UAE advisors on WhatsApp — usually replies within an hour.
          </p>
          {/* Pointer */}
          <span className="absolute -bottom-1.5 right-6 w-3 h-3 bg-white border-r border-b border-gray-100 rotate-45" />
        </div>
      )}

      {/* Floating action button */}
      <button
        type="button"
        onClick={handleClick}
        aria-label="Chat with White Paper on WhatsApp"
        title="Chat on WhatsApp"
        className="relative flex items-center justify-center w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white shadow-lg hover:shadow-2xl hover:scale-105 active:scale-95 transition-all"
        data-testid="whatsapp-float-btn"
      >
        {/* Pulsing halo */}
        <span
          className="absolute inset-0 rounded-full bg-[#25D366] opacity-60 animate-ping"
          style={{ animationDuration: '2.5s' }}
          aria-hidden="true"
        />
        <WhatsAppGlyph size={30} className="relative z-10" />
      </button>
    </div>
  );
};

export default WhatsAppFloat;
